import { DateOnly } from '@nx-superprep/utils';
import { LessonTile } from '@nx-superprep/study';
import { ContentTile } from '@nx-superprep/shared';
import { CompletionState, isSameCompletionState } from '../home/status/completion-state';
import { PerformanceChartRecord, PerformanceStatRecord, PerformanceStats, isSamePerformanceChartRecordArray as isSamePerformanceChartRecordArray, isSamePerformanceStatRecordArray as isSamePerformanceStatRecordArray } from '@nx-superprep/backend/firebase';
import { isEqual } from 'lodash-es';

export interface DrawerState {
  type:
    | 'performance-stats'
    | 'performance-charts'
    | 'library-lessons'
    | 'dayplan-lessons'
    | 'lesson-completion'
    | 'onboarding-lessons'
  headerState: DrawerHeaderState
  contentState: DrawerContentState
}

export interface DrawerTab {
  label: string
  title?: string
  subtitle?: string | (() => string)
  disabled?: boolean
  hideLabel?: boolean
}

export interface DrawerHeaderState {
  title?: string
  activeTab?: string
  tabs?: DrawerTab[]
  completion?: CompletionState
}

export interface DrawerContentState {
  title?: string
  date?: DateOnly
  selected?: string
  action?: (value: string) => Promise<boolean>
  select?: (value: LessonTile | PerformanceStats) => Promise<boolean>
  tiles?: LessonTile[] | ContentTile[]
  stats?: PerformanceStatRecord[]
  charts?: PerformanceChartRecord[]
  completion?: CompletionState
  lesson?: LessonTile
  isLoaded?: boolean
}

export const isSameDrawerHeaderState = (a: DrawerHeaderState|undefined, b: DrawerHeaderState|undefined): boolean => {
  return (
    a === b || (
      !!a &&
      !!b &&
      a.title === b.title &&
      a.activeTab === b.activeTab &&
      isSameCompletionState(a.completion, b.completion) &&
      isEqual(a.tabs, b.tabs)
    )
  )
}

export const isSameDrawerContentState = (a: DrawerContentState|undefined, b: DrawerContentState|undefined): boolean => {
  return (
    a === b || (
      !!a &&
      !!b &&
      a.title === b.title &&
      DateOnly.compare(a.date, b.date) === 0 &&
      a.selected === b.selected &&
      isEqual(a.tiles, b.tiles) &&
      isSamePerformanceChartRecordArray(a.charts ?? [], b.charts ?? []) &&
      isSamePerformanceStatRecordArray(a.stats ?? [], b.stats ?? [])
    )
  )
}

export const isSameOrNewerDrawerContentState = (a: DrawerContentState|undefined, b: DrawerContentState|undefined): boolean => {
  return (
    a === b || (
      !!a &&
      !!b &&
      a.title === b.title &&
      DateOnly.compare(a.date, b.date) <= 0 &&
      a.selected === b.selected &&
      isEqual(a.tiles, b.tiles) &&
      (((a.charts ?? []).length < (b.charts ?? []).length) || isSamePerformanceChartRecordArray(a.charts ?? [], b.charts ?? [])) &&
      (((a.stats ?? []).length < (b.stats ?? []).length) || isSamePerformanceStatRecordArray(a.stats ?? [], b.stats ?? []))
    )
  )
}

export const isSameDrawerState = (a: DrawerState|undefined, b: DrawerState|undefined): boolean => {
  return (
    a === b || (
      !!a &&
      !!b &&
      a.type === b.type &&
      isSameDrawerHeaderState(a.headerState, b.headerState) &&
      isSameDrawerContentState(a.contentState, b.contentState)
    )
  )
}

export const isSameOrNewerDrawerState = (a: DrawerState|undefined, b: DrawerState|undefined): boolean => {
  return (
    a === b || (
      !!a &&
      !!b &&
      a.type === b.type &&
      isSameDrawerHeaderState(a.headerState, b.headerState) &&
      isSameOrNewerDrawerContentState(a.contentState, b.contentState)
    )
  )
}
